import React from "react";
import { BrowserRouter, Switch } from "react-router-dom";
import ReactModal from "react-modal";
import {
  PublicClientApplication,
  EventType,
  InteractionStatus,
} from "@azure/msal-browser";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { AppRoute } from "./AppRoute";
import * as paths from "./paths";
import { generateRouteParams } from "./generateRouteParams";
import { AuthorizedRouter } from "./AuthorizedRouter";
import { msalConfig } from "features/authConfig"; // ✅ no .js so it matches your .d.ts module
import { CommunicationPreferences } from "features/communicationPreferences";
import { VerifyDemographics } from "features/login/VerifyDemographics";
import { Root } from "features/root";
import { ComponentCatalog } from "features/componentCatalog";
import { AppointmentBookingStart } from "features/scheduling/AppointmentBookingStart";
import { AppointmentExamDetails } from "features/scheduling/AppointmentExamDetails";
import { AppointmentSelectSlot } from "features/scheduling/AppointmentSelectSlot";
import { AppointmentSummary } from "features/scheduling/AppointmentSummary";
import { AppointmentAccountSetup } from "features/scheduling/AppointmentAccountSetup";
import {
  AppointmentFailure,
  AppointmentFailureV2,
} from "features/scheduling/AppointmentFailure";
import { AppointmentReviewV2 } from "features/scheduling/V2/AppointmentReviewV2";
import { AppointmentSuccessV2 } from "features/scheduling/V2/AppointmentSuccessV2";
import { AppointmentSuccessNewV2 } from "features/scheduling/V2/AppointmentSuccessNewV2";
import { AppointmentBookingStartV2 } from "features/scheduling/V2/AppointmentBookingStartV2";
import { KnownPatientPage } from "features/scheduling/V2/KnownPatientPage";
import { OrderTracker } from "features/scheduling/OrderTracker";
import { ValidateOfficeAPI } from "features/scheduling/ValidateOfficeAPI";
import { Offices } from "features/scheduling/Offices";
import { AppointmentReview } from "features/scheduling/AppointmentReview";
import { AppointmentSelectReturnStatus } from "features/scheduling/AppointmentSelectReturnStatus";
import { AppointmentSelectDoctorSlot } from "features/scheduling/AppointmentSelectDoctorSlot";
import { AppointmentSlotTaken } from "features/scheduling/AppointmentSlotTaken";
import {
  AppointmentSuccess,
  AppointmentSuccessNew,
} from "features/scheduling/AppointmentSuccess";
import { MagicLinkLanding } from "features/login/MagicLinkLanding";
import { ChooseTwoFactorMethod } from "features/login/ChooseTwoFactorMethod";
import { EnterTwoFactorCode } from "features/login/EnterTwoFactorCode";
import {
  PatientSelector,
  ConfirmPatient,
} from "features/login/PatientSelector";
import PatientSelectorContainer from "features/login/PatientSelector/PatientSelectorContainer";
import { AppointmentAuthorizationFailed } from "features/patientDashboard/AppointmentAuthorizationFailed";
import { PatientDashboardCAIM } from "features/patientDashboard/PatientDashboardCAIM";
import { LoginRedirectToast } from "features/patientDashboard/ErrorToast";
import { FullScreenLoader } from "components/FullScreenLoader/FullScreenLoader";
import { ConditionalRoute } from "features/routing/ConditionalRoute";
import { areDebugRoutesEnabled } from "featureFlags/areDebugRoutesEnabled";
import RootLanding from "features/root/RootLanding";
import PatientDashboardLoginRedirect from "features/root/PatientDashboardLoginRedirect";
import { useQueryParam } from "hooks/useQueryParam";

/**
 * MSAL bootstrap + recovery
 * - Must run BEFORE any route logic calls loginRedirect.
 * - Prevents "blank refresh loop" after returning from B2C.
 */
const MSAL_RESET_KEY = "ciam:msal_reset_at";
const MSAL_RESET_COOLDOWN_MS = 60_000;

const AppointmentBookingStartRoute: React.FC = () => {
  const [knownPatientParam] = useQueryParam("kp", null);
  const [portalParam] = useQueryParam("portal", null);
  const isKnownPatientRoute =
    knownPatientParam === "1" || portalParam === "1";

  return isKnownPatientRoute ? (
    <KnownPatientPage />
  ) : (
    <AppointmentBookingStartV2 />
  );
};

function clearMsalKeys(storage: Storage) {
  const prefixes = ["msal.", "interaction.", "cc."];
  const keysToRemove: string[] = [];

  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (!k) continue;
    if (prefixes.some((p) => k.startsWith(p))) keysToRemove.push(k);
  }

  keysToRemove.forEach((k) => storage.removeItem(k));
}

function resetMsalOnceAndReload() {
  const now = Date.now();
  const last = Number(sessionStorage.getItem(MSAL_RESET_KEY) || "0");
  if (last && now - last < MSAL_RESET_COOLDOWN_MS) return;

  sessionStorage.setItem(MSAL_RESET_KEY, String(now));

  clearMsalKeys(sessionStorage);
  clearMsalKeys(localStorage);

  // app key you set elsewhere
  sessionStorage.removeItem("postLoginRedirect");

  window.location.replace(window.location.pathname + window.location.search);
}

function isRecoverableMsalError(e: unknown): boolean {
  const msg = String((e as any)?.message || e || "");
  return (
    msg.includes("interaction_in_progress") ||
    msg.includes("endpoints_resolution_error") ||
    msg.includes("openid_config_error") ||
    msg.includes("Could not retrieve endpoints") ||
    msg.includes(".well-known")
  );
}

const MsalBootstrap: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, inProgress } = useMsal();
  const [ready, setReady] = React.useState(false);
  const [showLoginToast, setShowLoginToast] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        // supported in newer msal versions
        await (instance as any).initialize?.();
      } catch {
        // ignore
      }

      try {
        // ✅ finalize redirect response before any guards/routes can call loginRedirect
        await instance.handleRedirectPromise();
      } catch (e) {
        if (isRecoverableMsalError(e)) {
          resetMsalOnceAndReload();
          return;
        }
        // eslint-disable-next-line no-console
        console.log("handleRedirectPromise failed", e);
      }

      if (mounted) setReady(true);
    })();

    return () => {
      mounted = false;
    };
  }, [instance]);

  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  const isLoginLikePath =
    pathname === paths.root() ||
    pathname === paths.portalLogin() ||
    pathname === paths.old.patientDashboardLogin();

  React.useEffect(() => {
    if (!ready || inProgress !== InteractionStatus.None) {
      setShowLoginToast(true);
      return;
    }
    setShowLoginToast(false);
  }, [ready, inProgress]);

  // Don’t render routes while MSAL is processing an interaction
  if (!ready || inProgress !== InteractionStatus.None) {
    return (
      <>
        <FullScreenLoader />
        {isLoginLikePath && (
          <LoginRedirectToast
            isLoading={showLoginToast}
            delayMs={3000}
            onDismiss={() => setShowLoginToast(false)}
            onRetry={() => window.location.reload()}
          />
        )}
      </>
    );
  }

  return <>{children}</>;
};

export const msalInstance = new PublicClientApplication(msalConfig);

// Default to using the first account if no account is active on page load
if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
  msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}

msalInstance.addEventCallback((event) => {
  const payload = (event as any).payload;
  const account = payload?.account;

  if (
    (event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
      event.eventType === EventType.SSO_SILENT_SUCCESS) &&
    account
  ) {
    msalInstance.setActiveAccount(account);
  }
});

export function AppRouter() {
  ReactModal.setAppElement("#root");

  return (
    <BrowserRouter>
      <MsalProvider instance={msalInstance}>
        <MsalBootstrap>
          <Switch>
            {/* Scheduling */}
            <AppRoute
              exact
              path={paths.appointmentSuccessV2(generateRouteParams("officeId"))}
            >
              <AppointmentSuccessV2 />
            </AppRoute>
            <AppRoute
              exact
              path={paths.appointmentSuccessNewV2(generateRouteParams("officeId"))}
            >
              <AppointmentSuccessNewV2 />
            </AppRoute>
            <AppRoute
              exact
              path={paths.appointmentsReviewV2(generateRouteParams("officeId"))}
            >
              <AppointmentReviewV2 />
            </AppRoute>
            <AppRoute
              exact
              path={paths.appointmentBookingStartV2(generateRouteParams("officeId"))}
            >
              <AppointmentBookingStartRoute />
            </AppRoute>
            <AppRoute
              exact
              path={paths.appointmentBookingStart(generateRouteParams("officeId"))}
            >
              <AppointmentBookingStart />
            </AppRoute>
            <AppRoute
              exact
              path={paths.appointmentsSelectType(generateRouteParams("officeId"))}
            >
              <AppointmentExamDetails />
            </AppRoute>
            <AppRoute
              exact
              path={paths.appointmentsFailureV2(generateRouteParams("officeId"))}
            >
              <AppointmentFailureV2 />
            </AppRoute>
            <AppRoute
              exact
              path={paths.appointmentsSelectReturnStatus(
                generateRouteParams("officeId", "examType")
              )}
            >
              <AppointmentSelectReturnStatus />
            </AppRoute>
            <AppRoute
              exact
              path={paths.appointmentsAvailableAppointments(
                generateRouteParams("officeId", "examType", "patientReturnStatus")
              )}
            >
              <AppointmentSelectSlot />
            </AppRoute>
            <AppRoute
              exact
              path={paths.appointmentsAvailableAppointmentsDoctor(
                generateRouteParams(
                  "officeId",
                  "examType",
                  "patientReturnStatus",
                  "doctorId"
                )
              )}
            >
              <AppointmentSelectDoctorSlot />
            </AppRoute>
            <AppRoute
              exact
              path={paths.appointmentsReview(
                generateRouteParams("officeId", "examType", "patientReturnStatus")
              )}
            >
              <AppointmentReview />
            </AppRoute>
            <AppRoute
              exact
              path={paths.appointmentsSummary(
                generateRouteParams("officeId", "examType", "patientReturnStatus")
              )}
            >
              <AppointmentSummary />
            </AppRoute>
            <AppRoute
              exact
              path={paths.appointmentsAccountSetup(
                generateRouteParams("officeId", "examType", "patientReturnStatus")
              )}
            >
              <AppointmentAccountSetup />
            </AppRoute>
            <AppRoute
              exact
              path={paths.appointmentsSuccess(
                generateRouteParams("officeId", "examType", "patientReturnStatus")
              )}
            >
              <AppointmentSuccess />
            </AppRoute>
            <AppRoute
              exact
              path={paths.appointmentsSuccessNew(
                generateRouteParams("officeId", "examType", "patientReturnStatus")
              )}
            >
              <AppointmentSuccessNew />
            </AppRoute>
            <AppRoute
              exact
              path={paths.appointmentsFailure(
                generateRouteParams("officeId", "examType", "patientReturnStatus")
              )}
            >
              <AppointmentFailure />
            </AppRoute>
            <AppRoute
              exact
              path={paths.appointmentsSlotTaken(
                generateRouteParams("officeId", "examType", "patientReturnStatus")
              )}
            >
              <AppointmentSlotTaken />
            </AppRoute>
            <AppRoute
              exact
              path={paths.OrderTrackerParams(generateRouteParams("orderUID"))}
            >
              <OrderTracker />
            </AppRoute>
            <AppRoute
              exact
              path={paths.ProviderSlotParams(generateRouteParams("officeId"))}
            >
              <ValidateOfficeAPI />
            </AppRoute>

            {/* Authorization */}
            <AppRoute
              exact
              path={[
                paths.appointmentAuthorizationFailed(),
                paths.old.appointmentAuthorizationFailed(),
              ]}
            >
              <AppointmentAuthorizationFailed />
            </AppRoute>
            <AppRoute
              exact
              path={[
                paths.verifyDemographics(generateRouteParams("token")),
                paths.old.verifyDemographics(generateRouteParams("token")),
              ]}
            >
              <VerifyDemographics />
            </AppRoute>
            <AppRoute
              exact
              path={[
                paths.magicLinkLanding(generateRouteParams("token")),
                paths.old.magicLinkLanding(generateRouteParams("token")),
              ]}
            >
              <MagicLinkLanding />
            </AppRoute>
            <AppRoute exact path={[paths.portalLogin(), paths.old.patientDashboardLogin()]}>
              <PatientDashboardLoginRedirect />
            </AppRoute>
            <AppRoute exact path={paths.chooseTwoFactorMethod({})}>
              <ChooseTwoFactorMethod />
            </AppRoute>
            <AppRoute
              exact
              path={paths.enterTwoFactorCode(generateRouteParams("phoneNumberId"))}
            >
              <EnterTwoFactorCode />
            </AppRoute>
            <AppRoute exact path={paths.patientSelector()}>
              <PatientSelectorContainer />
            </AppRoute>
            <AppRoute exact path={paths.confirmPatient()}>
              <ConfirmPatient />
            </AppRoute>

            {/* Portal */}
            <AppRoute
              exact
              path={[
                paths.communicationPreferences(generateRouteParams("patientId")),
                paths.old.communicationPreferences(generateRouteParams("patientId")),
              ]}
            >
              <CommunicationPreferences />
            </AppRoute>
            <AppRoute path={[paths.portalFragment, paths.oldManageAppointmentFragment]}>
              <AuthorizedRouter />
            </AppRoute>
            <AppRoute exact path={paths.portalCaimHome()}>
              <PatientDashboardCAIM />
            </AppRoute>

            {/* Remainder */}
            <ConditionalRoute
              enabled={areDebugRoutesEnabled()}
              exact
              path={paths.componentCatalog()}
            >
              <ComponentCatalog />
            </ConditionalRoute>
            <ConditionalRoute
              enabled={areDebugRoutesEnabled()}
              exact
              path={paths.offices()}
            >
              <Offices />
            </ConditionalRoute>
            <ConditionalRoute
              enabled={areDebugRoutesEnabled()}
              exact
              path={paths.rootLogin()}
            >
              <Root />
            </ConditionalRoute>
            <AppRoute exact path={paths.pxpHome()}>
              <Root />
            </AppRoute>
            <AppRoute exact path={paths.root()}>
              <RootLanding />
            </AppRoute>
            <AppRoute path="*">
              <Root />
            </AppRoute>
          </Switch>
        </MsalBootstrap>
      </MsalProvider>
    </BrowserRouter>
  );
}
